import {
    HTMLAttributes,
    type AnchorHTMLAttributes,
    type ComponentPropsWithRef,
    type MouseEvent,
    type ReactNode,
} from 'react';
import type { LinkProps } from 'react-router';
import { Link } from 'react-router';
import Icon, { type IconNames } from '~/components/ui/Icon';
import Tooltip from '~/components/ui/Tooltip';
import '~/styles/components/new/icon-action.scss';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames, iconColorVar, type IconColor } from '~/utils/styles';

type IconCoreColor = Extract<IconColor, `core-${string}`>;
type IconSupportColor = Extract<IconColor, `support-${string}`>;

/**
 * BaseIconAction is an internal implementation used by IconAction and IconActionLink
 * It dangerously receive children, do not export it out from this module.
 */

function BaseIconAction({
    children,
    title,
    className = '',
    ref,
    ...divProps
}: ComponentPropsWithRef<'div'> & {
    children: ReactNode;
    title?: string;
    className?: string;
}) {
    return (
        <Tooltip title={title}>
            <div
                ref={ref}
                {...divProps}
                className={classNames({
                    'icon-action icon-action-wrapper': true,
                    [className]: Boolean(className),
                })}>
                {children}
            </div>
        </Tooltip>
    );
}

/**
 * IconAction
 */
type TitleOrClickOrBoth =
    | { title: string; onClick?: (ev: MouseEvent<HTMLElement>) => void }
    | { title?: string; onClick: (ev: MouseEvent<HTMLElement>) => void }
    | { title: string; onClick: (ev: MouseEvent<HTMLElement>) => void };

type iconActionColorVars = IconCoreColor | IconSupportColor;

export type IconActionProps = {
    icon: IconNames;
    iconSize?: 'medium' | 'small';
    targetSize?: 'medium' | 'small';
    disabled?: boolean;
    className?: string;
    colorVar?: iconActionColorVars;
    hoverColorVar?: iconActionColorVars;
} & TitleOrClickOrBoth &
    DataTestAttributes;

const IconSizes = {
    medium: 16,
    small: 12,
};
const TargetSizes = {
    large: 40,
    medium: 32,
    small: 24,
};

export default function IconAction({
    icon,
    iconSize = 'medium',
    targetSize = 'small',
    disabled = false,
    onClick,
    title,
    className = '',
    colorVar = 'core-primary',
    hoverColorVar,
    ref,
    'data-test-component': testComponent = 'new/IconAction',
    'data-test-name': testName,
    ...props
}: IconActionProps & { ref?: React.Ref<HTMLDivElement> } & HTMLAttributes<HTMLDivElement>) {
    return (
        <BaseIconAction
            ref={ref}
            title={title}
            className={className}
            style={{ '--hovered-icon-color': `var(${iconColorVar(hoverColorVar ?? colorVar)})` }}>
            <div
                {...props}
                data-test-component={testComponent}
                data-test-name={testName}
                role='button'
                tabIndex={disabled || !onClick ? -1 : 0}
                onClick={!disabled ? onClick : undefined}
                className={classNames({
                    'icon-action-target-area': true,
                    'cursor-pointer': Boolean(onClick) && !disabled,
                })}
                style={{
                    width: TargetSizes[targetSize],
                }}
            />
            <Icon
                icon={icon}
                size={IconSizes[iconSize]}
                className={disabled ? 'icon-action-disabled' : undefined}
                colorVar={colorVar}></Icon>
        </BaseIconAction>
    );
}

/**
 * IconActionLink
 * is an IconAction with a target area as Link
 */

export type IconActionLinkProps = Omit<IconActionProps, 'onClick' | 'title'> &
    LinkProps &
    AnchorHTMLAttributes<HTMLAnchorElement>;

export function IconActionLink({
    // icon action props
    icon,
    iconSize = 'medium',
    targetSize = 'medium',
    disabled = false,
    title,
    className = '',
    colorVar = 'core-primary',
    // other props (link + anchor element)
    ...props
}: IconActionLinkProps) {
    return (
        <BaseIconAction
            title={title}
            className={className}>
            {/* replaces Link by div since in order to prevent click on Link when disabled, we can use pointer-events: none;
            but that also prevents to trigger the tooltip on hover */}
            {disabled ? (
                <div
                    className='icon-action-target-area'
                    style={{
                        width: TargetSizes[targetSize],
                    }}
                />
            ) : (
                <Link
                    className='icon-action-target-area'
                    style={{
                        width: TargetSizes[targetSize],
                    }}
                    {...props}
                    // we need to stop propagation to prevent some parent to catch and cancel the event. ¿?
                    // that's the case for action cell into tables in the app (note that Link works fine in Storybook without this hack).
                    onClick={(e) => {
                        e.stopPropagation();
                        props.onClick?.(e);
                    }}
                />
            )}
            <Icon
                icon={icon}
                size={IconSizes[iconSize]}
                className={disabled ? 'icon-action-disabled' : undefined}
                colorVar={colorVar}
            />
        </BaseIconAction>
    );
}
